/**
 * WellboreSchematic — a hand-SVG wellbore section (§6, "Schematics = hand-SVG",
 * §1 component kit). Concentric casing/liner strings drawn to their shoe depth
 * on a LINEAR measured-depth axis with 0 at the top (surface) and TD at the
 * bottom, each string annotated with a leader-lined callout (size / grade /
 * connection / shoe MD·TVD). The current bit depth is a dashed marker line.
 *
 * Reuses the OXBO-x schematic idiom (depth-linear vertical scale, nested rects
 * to shoe, right-hand callout column). Everything is token-only — no hex. The
 * geometry is DERIVED from the passed strings (widest OD = outermost), not the
 * wireframe's fixed coordinates, so it renders any casing program.
 */
import { useMemo } from "react";

/** One string to draw — depths already display-converted (labels carry units). */
export interface SchematicString {
  id: string;
  name: string;
  odIn: number | null;
  shoeMd: number | null;
  shoeMdLabel: string;
  shoeTvdLabel: string;
  gradeLabel: string;
  connectionLabel: string;
  running: boolean;
  carried: boolean;
}

export interface WellboreSchematicProps {
  strings: SchematicString[];
  /** Current bit depth in MD (same profile as shoe MD); null = no marker. */
  bitDepthMd?: number | null;
  bitDepthLabel?: string;
  ariaLabel: string;
  labels: {
    wellhead: string;
    openHole: string;
    bitDepth: string;
    shoeMd: string;
    shoeTvd: string;
    running: string;
  };
}

// ── SVG frame geometry (viewBox units) ──
const VB_W = 1200;
const VB_H = 820;
const AXIS_X = 118; // depth axis line
const TOP_Y = 66; // depth = 0
const BOT_Y = 766; // depth = max
const BORE_CENTER = 420; // wellbore centreline x
const BORE_HALF_MAX = 92; // half-width of the outermost (widest-OD) string
const BORE_HALF_MIN = 30; // half-width of the innermost string
const CALLOUT_X = 770;
const CALLOUT_W = 360;

export function WellboreSchematic({
  strings,
  bitDepthMd,
  bitDepthLabel,
  ariaLabel,
  labels,
}: WellboreSchematicProps) {
  const model = useMemo(() => buildModel(strings, bitDepthMd), [strings, bitDepthMd]);
  const y = (depth: number) => TOP_Y + (depth / model.maxDepth) * (BOT_Y - TOP_Y);

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMin meet"
      role="img"
      aria-label={ariaLabel}
    >
      {/* depth axis: linear, 0 at top */}
      <g fontFamily="var(--font-mono)" fontSize="13" fill="var(--dim)">
        <line x1={AXIS_X} y1={TOP_Y} x2={AXIS_X} y2={BOT_Y} stroke="var(--ink)" strokeWidth="1.5" />
        <text x="20" y="46" fontSize="14" fill="var(--head)">
          MD
        </text>
        {model.ticks.map((d) => (
          <g key={d}>
            <line x1={AXIS_X - 12} y1={y(d)} x2={AXIS_X} y2={y(d)} stroke="var(--dim)" />
            <text x={AXIS_X - 20} y={y(d) + 4} textAnchor="end">
              {formatTick(d)}
            </text>
          </g>
        ))}
      </g>

      {/* surface / wellhead cap */}
      <rect
        x={BORE_CENTER - BORE_HALF_MAX}
        y="50"
        width={BORE_HALF_MAX * 2}
        height="16"
        rx="3"
        fill="var(--surface-3)"
        stroke="var(--ink)"
        strokeWidth="1.5"
      />
      <text x={BORE_CENTER} y="42" textAnchor="middle" fontSize="13" fill="var(--head)">
        {labels.wellhead}
      </text>

      {/* concentric strings (outermost first, so inner draws over) */}
      {model.drawn.map((s, i) => {
        const shoeY = y(s.shoeMd);
        const isInner = i === model.drawn.length - 1;
        const stroke = isInner ? "var(--oxbo-z)" : "var(--ink)";
        return (
          <g key={s.id}>
            {/* casing walls */}
            <rect
              x={BORE_CENTER - s.half}
              y={TOP_Y}
              width="8"
              height={shoeY - TOP_Y}
              fill={stroke}
              opacity={s.carried ? 0.85 : 1}
            />
            <rect
              x={BORE_CENTER + s.half - 8}
              y={TOP_Y}
              width="8"
              height={shoeY - TOP_Y}
              fill={stroke}
              opacity={s.carried ? 0.85 : 1}
            />
            {/* shoe (flared foot) */}
            <path
              d={`M ${BORE_CENTER - s.half - 12} ${shoeY} L ${BORE_CENTER + s.half + 12} ${shoeY} L ${BORE_CENTER + s.half - 4} ${shoeY + 20} L ${BORE_CENTER - s.half + 4} ${shoeY + 20} Z`}
              fill={stroke}
            />
            {/* leader line + callout */}
            <line
              x1={BORE_CENTER + s.half}
              y1={shoeY}
              x2={CALLOUT_X}
              y2={s.calloutY + 30}
              stroke={isInner ? "var(--oxbo-z)" : "var(--line)"}
              strokeWidth="1"
            />
            <rect
              x={CALLOUT_X}
              y={s.calloutY}
              width={CALLOUT_W}
              height="92"
              rx="8"
              fill="var(--surface-2)"
              stroke={isInner ? "var(--oxbo-z)" : "var(--ink)"}
              strokeWidth={isInner ? 1.5 : 1.25}
            />
            <text
              x={CALLOUT_X + 18}
              y={s.calloutY + 26}
              fontFamily="var(--font-sans)"
              fontSize="15"
              fontWeight="700"
              fill={isInner ? "var(--oxbo-z)" : "var(--head)"}
            >
              {s.name}
              {s.odLabel ? ` — ${s.odLabel}` : ""}
            </text>
            <text
              x={CALLOUT_X + 18}
              y={s.calloutY + 47}
              fontFamily="var(--font-mono)"
              fontSize="12.5"
              fill="var(--ink)"
            >
              {[s.gradeLabel, s.connectionLabel].filter(Boolean).join(" · ")}
            </text>
            <text
              x={CALLOUT_X + 18}
              y={s.calloutY + 66}
              fontFamily="var(--font-mono)"
              fontSize="12.5"
              fill="var(--ink)"
            >
              {labels.shoeMd} {s.shoeMdLabel}
              {s.shoeTvdLabel ? ` · ${labels.shoeTvd} ${s.shoeTvdLabel}` : ""}
            </text>
            {s.running ? (
              <text
                x={CALLOUT_X + 18}
                y={s.calloutY + 84}
                fontFamily="var(--font-mono)"
                fontSize="12.5"
                fill="var(--good)"
              >
                {labels.running}
              </text>
            ) : null}
          </g>
        );
      })}

      {/* open hole below the innermost shoe */}
      {model.openHoleY != null ? (
        <>
          <path
            d={`M ${BORE_CENTER - BORE_HALF_MIN} ${model.openHoleY} L ${BORE_CENTER + BORE_HALF_MIN} ${model.openHoleY} L ${BORE_CENTER + BORE_HALF_MIN - 20} ${BOT_Y} L ${BORE_CENTER - BORE_HALF_MIN + 20} ${BOT_Y} Z`}
            fill="var(--surface-2)"
            stroke="var(--ink)"
            strokeWidth="1.5"
            strokeDasharray="3,3"
          />
          <text x={BORE_CENTER} y={BOT_Y + 16} textAnchor="middle" fontSize="12" fill="var(--dim)">
            {labels.openHole}
          </text>
        </>
      ) : null}

      {/* current bit depth marker */}
      {model.bitY != null ? (
        <g>
          <line
            x1={AXIS_X + 22}
            y1={model.bitY}
            x2={CALLOUT_X}
            y2={model.bitY}
            stroke="var(--warn)"
            strokeWidth="2"
            strokeDasharray="7,4"
          />
          <circle cx={BORE_CENTER} cy={model.bitY} r="5" fill="var(--warn)" />
          <text
            x={AXIS_X + 26}
            y={model.bitY - 6}
            fontFamily="var(--font-mono)"
            fontSize="12"
            fill="var(--warn)"
          >
            {labels.bitDepth}
            {bitDepthLabel ? ` ${bitDepthLabel}` : ""}
          </text>
        </g>
      ) : null}
    </svg>
  );
}

interface DrawnString extends SchematicString {
  shoeMd: number;
  half: number;
  odLabel: string;
  calloutY: number;
}

interface SchematicModel {
  maxDepth: number;
  ticks: number[];
  drawn: DrawnString[];
  openHoleY: number | null;
  bitY: number | null;
}

/**
 * Turn the strings into a drawable model: sort outer→inner by OD, assign a
 * half-width per ring, a shoe depth, a callout slot, and derive the axis scale.
 * Strings with no shoe depth are dropped from the geometry (they still list in
 * the table).
 */
function buildModel(strings: SchematicString[], bitDepthMd?: number | null): SchematicModel {
  const withShoe = strings.filter(
    (s): s is SchematicString & { shoeMd: number } => s.shoeMd != null,
  );
  // Deepest shoe / bit anchors the axis; pad 5% so the deepest shoe isn't flush.
  const deepest = Math.max(
    1,
    ...withShoe.map((s) => s.shoeMd),
    ...(bitDepthMd != null ? [bitDepthMd] : []),
  );
  const maxDepth = niceCeil(deepest * 1.05);
  const y = (depth: number) => TOP_Y + (depth / maxDepth) * (BOT_Y - TOP_Y);

  // Order outer→inner: widest OD first. Unknown OD sinks to the inside.
  const ordered = [...withShoe].sort((a, b) => (b.odIn ?? 0) - (a.odIn ?? 0));
  const n = ordered.length;
  const drawn: DrawnString[] = ordered.map((s, i) => {
    const half =
      n <= 1 ? BORE_HALF_MAX : BORE_HALF_MAX - ((BORE_HALF_MAX - BORE_HALF_MIN) * i) / (n - 1);
    return {
      ...s,
      half,
      odLabel: s.odIn != null ? `${s.odIn}″` : "",
      calloutY: 78 + i * 108,
    };
  });

  const innermost = drawn[drawn.length - 1];
  const innerShoeY = innermost ? y(innermost.shoeMd) : null;
  const openHoleY =
    innerShoeY != null && innermost && innermost.shoeMd < maxDepth * 0.95 ? innerShoeY + 20 : null;

  return {
    maxDepth,
    ticks: axisTicks(maxDepth),
    drawn,
    openHoleY,
    bitY: bitDepthMd != null ? y(bitDepthMd) : null,
  };
}

/** Round a max depth up to a clean axis extent. */
function niceCeil(v: number): number {
  const step = v > 10000 ? 2500 : v > 2000 ? 1000 : v > 500 ? 250 : 100;
  return Math.ceil(v / step) * step;
}

/** Evenly-spaced tick depths (including 0 and max). */
function axisTicks(max: number): number[] {
  const step = max > 10000 ? 2500 : max > 2000 ? 1000 : max > 500 ? 250 : 100;
  const out: number[] = [];
  for (let d = 0; d <= max + 1; d += step) out.push(d);
  return out;
}

function formatTick(d: number): string {
  return d.toLocaleString("en-US");
}
